import * as React from 'react';
import { pb } from '@/lib/pb';
import { COLLECTIONS, isStaff, type AppUser } from '@/lib/types';

interface AuthState {
  user: AppUser | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthCtx = React.createContext<AuthState | null>(null);

function currentRecord(): AppUser | null {
  const rec = pb.authStore.record as unknown as AppUser | null;
  return rec ?? null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<AppUser | null>(() => currentRecord());

  React.useEffect(() => {
    const unsub = pb.authStore.onChange(() => setUser(currentRecord()));
    return () => unsub();
  }, []);

  const login = React.useCallback(async (email: string, password: string) => {
    const res = await pb.collection(COLLECTIONS.users).authWithPassword(email, password);
    const rec = res.record as unknown as AppUser;
    // PB authenticates any valid credential; enforce our staff gate here.
    if (!isStaff(rec)) {
      pb.authStore.clear();
      throw new Error('Dieses Konto hat keinen Zugang zum Admin-Bereich (deaktiviert oder falsche Rolle).');
    }
  }, []);

  const logout = React.useCallback(() => {
    pb.authStore.clear();
  }, []);

  return <AuthCtx.Provider value={{ user, login, logout }}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = React.useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
